import { kbLog } from './keyboardReframeDebug';
import { releaseSpacer, addSpacerPadding, trimSpacerPadding } from './inlineKeyboardSpacer';

/**
 * Manufacture the scroll range a reveal needs with the guard's bottom-padding
 * spacer — extracted VERBATIM from `inlineKeyboardGuard.js` (500-line cap,
 * zero behaviour change). Returns false when the scroller is content-hugging
 * (the spacer was reverted and the guard must skip).
 *
 * Must run BEFORE setting scrollTop — a scrollTop beyond the current range is
 * silently clamped. `style.paddingBottom` is where React itself writes, so
 * reading it gives the currently-applied value; we add to it and put the exact
 * original string back on restore. (AppShell's scroller style object carries
 * no paddingBottom, so React has no value here to fight us over.)
 */
export function fillRevealSpacer(scroller, plan, target) {
  const preClient = scroller.clientHeight;
  addSpacerPadding(scroller, plan.spacer);
  // HUG-CONTENT PROBE (device capture 2026-08-21, delete-client confirm):
  // in a content-hugging box (a maxHeight-capped modal panel), bottom
  // padding INFLATES THE BOX instead of creating scroll range — the
  // spacer grew the confirm card from ~214px to its 785px viewport cap,
  // scrolled its title away and left a giant white void. If the box
  // grew, this container can never host a spacer reveal: revert
  // everything and let the modal system (anticipation/glide/curtain)
  // own the field instead.
  if (scroller.clientHeight > preClient + 1) {
    releaseSpacer();
    kbLog('inlineRevealSkip', { reason: 'hug-content' });
    return false;
  }
  // EXACT fill, v2 (device captures 2026-08-21 ×2, add-employee drawer):
  // in an under-FILLED fixed box, padding first fills the content-to-box
  // gap before creating ANY range — and while range reads 0, the
  // remaining gap is UNMEASURABLE (a `target − range` top-up under-adds:
  // second capture landed 26 of 164 and iOS panned the rest). So when
  // range is still 0, escalate by clientHeight — a hard upper bound on
  // any possible fill gap — which guarantees range ≥ target in ONE step,
  // then TRIM the overshoot so the void is exactly what the reveal
  // needs. When range is already > 0 the gap is fully consumed and the
  // deficit adds 1:1, so the plain top-up is exact.
  let range = scroller.scrollHeight - scroller.clientHeight;
  if (range < target) {
    addSpacerPadding(scroller, (target - range) + (range <= 0 ? scroller.clientHeight : 0));
    range = scroller.scrollHeight - scroller.clientHeight;
    const excess = range - target;
    if (excess > 0) trimSpacerPadding(scroller, excess);
    kbLog('inlineRevealSpacerFill', {
      range: Math.round(scroller.scrollHeight - scroller.clientHeight),
      target,
    });
  }
  kbLog('inlineRevealSpacer', { px: plan.spacer });
  return true;
}
